import { useEffect, useMemo, useState } from "react";
import { ProductDetail_Folder_PhotoListEle_Interface, ProductDetail_Folder_FolderListEle_Interface, ProductDetail_Interface, ProductDetail_OptionListEle_Interface, ProductDetail_productDetailData_Interface, ProductDetail_SelectedOptionListEle_Interface, ProductDetail_SelectInformationDate_AddedInfo_Interface, ProductDetail_SelectInformationDate_Address_Interface, ProductDetail_SelectInformationDate_Interface } from "../static/interface/ProductDetail.interface";
import { customParseInt, dateObjectManagerClass, dev_console } from "../../../Common/ts/commonTools";
import { PRODUCT_STATE_CODE_OBJ, PRODUCT_TYPE_CODE_OBJ } from "../static/ts/const";

const dateObjectManager = new dateObjectManagerClass();

const useProductDetailSetting = (
    {
        productDetailData,
    }:{
        productDetailData: ProductDetail_Interface | null;
    }
) => {
    // 수정하는 목적으로 사용되는 주소 - 사용자 인터랙션으로 변경되는 값이므로 useState 유지
    const [tempAddressList, setTempAddressList] = useState<ProductDetail_SelectInformationDate_Address_Interface[]>([]);

    // 기본 정보
    const productDetailInfoData = useMemo<ProductDetail_productDetailData_Interface | null>(() =>
        productDetailData?.product_detail_data || null
    , [productDetailData]);

    // 제품 수량
    const productQty = useMemo(() => {
        const qty = customParseInt(productDetailData?.product_detail_data?.product_qty);
        return typeof qty === "number" ? qty : 0;
    }, [productDetailData]);

    // 선택가능한 옵션들
    const productOptionDataList = useMemo<ProductDetail_OptionListEle_Interface[]>(() =>
        productDetailData?.product_option_data || []
    , [productDetailData]);

    // 폴더 리스트와 단일 여부
    const { folderList, isSingle } = useMemo(() => {
        const m_folderListData = [...productDetailData?.folder_list_data || []];
        const is_single = m_folderListData.length === 1 && m_folderListData[0]?.folder_id === 0;

        if(is_single){
            m_folderListData[0].folder_name = "사진 업로드";
        }

        return {
            folderList: m_folderListData,
            isSingle: is_single
        };
    }, [productDetailData]);

    // 선택한 옵션들
    const selectOptionDataList = useMemo<ProductDetail_SelectedOptionListEle_Interface[]>(() =>
        productDetailData?.select_option_data || []
    , [productDetailData]);

    // 선택한 사진 목록
    const selectPhotoList = useMemo<ProductDetail_Folder_PhotoListEle_Interface[]>(() =>
        productDetailData?.select_photo_list || []
    , [productDetailData]);

    // 편집 진행중인 사진 목록
    const editPhotoList = useMemo<ProductDetail_Folder_PhotoListEle_Interface[]>(() =>
        productDetailData?.edit_photo_list || []
    , [productDetailData]);

    // 선택 정보
    const selectInformationDate = useMemo<ProductDetail_SelectInformationDate_Interface | null>(() =>
        productDetailData?.select_information_data?.[0] || null
    , [productDetailData]);

    // 추가 정보
    const addedInfo = useMemo<ProductDetail_SelectInformationDate_AddedInfo_Interface | null>(() => {
        if(!selectInformationDate) return null;

        const l_baby_birth_date = new Date(selectInformationDate.baby_birth_day);
        const l_baby_birth_day_weekday = dateObjectManager.getWeekday(l_baby_birth_date);
        const l_baby_birth_day = `${selectInformationDate.baby_birth_day} (${l_baby_birth_day_weekday})`;

        return {
            baby_name: selectInformationDate.baby_name,
            baby_birth_day: l_baby_birth_day,
            baby_birth_time: selectInformationDate.baby_birth_time,
            baby_height: selectInformationDate.baby_height,
            baby_weight: selectInformationDate.baby_weight,
            common_memo: selectInformationDate.common_memo,
        };
    }, [selectInformationDate]);

    // 주소 목록
    const addressList = useMemo<ProductDetail_SelectInformationDate_Address_Interface[]>(() =>
        productDetailData?.select_addr_list || []
    , [productDetailData]);

    // 선택된 주소 수량
    const totalAddressQty = useMemo(() =>
        tempAddressList.reduce((acc, curr) => acc + curr.product_qty, 0)
    , [tempAddressList]);

    // 재업로드 가능 여부
    const isCanReupload = useMemo(() =>
        selectInformationDate?.status_cd === PRODUCT_STATE_CODE_OBJ.application_in_progress
        && (selectInformationDate?.request_num && selectInformationDate.request_num < 2) ? true : false
    , [selectInformationDate]);

    // 앨범 여부
    const isAlbum = useMemo(() =>
        productDetailInfoData?.product_type === PRODUCT_TYPE_CODE_OBJ.album
    , [productDetailInfoData]);

    // ProductRequestEdit에서 처음 신청하는 제품의 경우 초기에 빈 주소같을 출력하기 위해 addressList값이 없으면 빈 값을 추가한다.
    useEffect(() => {
        if(addressList.length > 0){
            setTempAddressList(addressList);
        }else{
            setTempAddressList([
                {
                    addr: "",
                    addr_detail: "",
                    product_qty: 1,
                    recipient_phone: "",
                    recipient_name: "",
                    building_no: "",
                    zip_code: "",
                }
            ]);
        }
    }, [addressList]);

    return({
        productDetailInfoData,
        productOptionDataList,
        folderList,
        initialSelectOptionDataList: selectOptionDataList,
        initialSelectPhotoList: selectPhotoList,
        initialEditPhotoList: editPhotoList,
        addedInfo,
        addressList,
        isSingle,
        isAlbum,
        selectInformationDate,
        isCanReupload,
        tempAddressList,
        setTempAddressList,
        productQty,
        totalAddressQty,
    })
}

export default useProductDetailSetting;